import {
  Banner,
  BoundRichTextField,
  BoundSelectField,
  BoundTextField,
  Button,
  Css,
  FormHeading,
  FormLines,
  SubmitButton,
  useComputed,
  useSuperDrawer,
} from "@homebound/beam";
import { SaveItemInput } from "src/generated/graphqlTypes";
import { useItemEditorTabMetadataQuery } from "src/routes/admin/items/ItemEditorTab.generated";
import { AdminItemFragment, useSaveItemMutation } from "src/routes/admin/items/ItemsPage.generated";
import { ObjectConfig, required, useFormState } from "src/utils/formState";
import { BoundMaterialLeadTimeConfigFields } from "../components/BoundMaterialLeadTimeConfigFields";
import { BoundOptionPricingConfigFields } from "../components/BoundOptionPricingConfigFields";
import {
  MaterialLeadTimeFormValue,
  OptionPricingFormValue,
  initializeMaterialLeadTimeConfigFields,
  materialLeadTimeConfigConfig,
  maybeMaterialLeadTimeConfig,
  maybeOptionPricingConfig,
  optionPricingConfigConfig,
} from "../utils";

export type ItemEditorProps = {
  costCodes: { id: string; displayName: string; version: number }[];
  selectedItem: AdminItemFragment | undefined;
  unitsOfMeasure: { id: string; name: string }[];
  // TODO: Remove once Material Catalog is released.
  // Adding "isMaterialCatalogEnabled" because the drawer opens outside of the Feature Flag Context Provider.
  isMaterialCatalogEnabled: boolean | undefined;
  isOptionCatalogV2Enabled?: boolean;
  isReadonly: boolean;
};

export function ItemEditorTab(props: ItemEditorProps) {
  const { costCodes, selectedItem, unitsOfMeasure, isOptionCatalogV2Enabled, isReadonly } = props;
  const { data } = useItemEditorTabMetadataQuery();
  const { designPackageLocation: dpLocationDetails } = data?.enumDetails || {};
  const isNew = selectedItem === undefined;
  const { closeDrawer } = useSuperDrawer();

  const formState = useFormState({
    config: formConfig,
    readOnly: isReadonly,
    init: {
      input: selectedItem,
      map: (selectedItem) => {
        const {
          costCode,
          unitOfMeasure,
          materialAttributeDimensions,
          designPackageLocation,
          optionPricingConfig,
          materialLeadTimeConfig,
          ...others
        } = selectedItem;
        // We don't need to preserve the id attribute, the backend will handle it as an update
        return {
          ...others,
          designPackageLocation: designPackageLocation.code,
          costCodeId: costCode.id,
          unitOfMeasureId: unitOfMeasure.id,
          optionPricingConfig: selectedItem.optionPricingConfig ?? {},
          materialLeadTimeConfig: { ...initializeMaterialLeadTimeConfigFields(selectedItem.materialLeadTimeConfig) },
        };
      },
    },
  });
  const [saveItem] = useSaveItemMutation();

  const isItemsV2 = useComputed(() => {
    const ccId = formState.costCodeId.value;
    return costCodes.find((cc) => cc.id === ccId)?.version === 2;
  }, [formState]);

  return (
    <>
      {isReadonly && (
        <div css={Css.mb3.$}>
          <Banner message={"You do not have permission to edit items"} type="info" />
        </div>
      )}

      <div css={Css.df.fdc.gap2.$}>
        <FormHeading title="Details" isFirst />
        <FormLines width="full">
          <BoundTextField field={formState.name} />
          <BoundSelectField
            field={formState.costCodeId}
            options={costCodes}
            getOptionLabel={(o) => `${o.displayName} V${o.version}`}
            getOptionValue={(o) => o.id}
            // We don't currently support moving items across cost codes, although
            // it might actually work, and is something retool allows already? Not sure.
            readOnly={!isNew}
          />
          {isItemsV2 && <BoundTextField field={formState.materialCodePrefix} />}
          <BoundSelectField field={formState.unitOfMeasureId} options={unitsOfMeasure} readOnly={!isNew} />
          <BoundSelectField
            label="Design Package Location"
            field={formState.designPackageLocation}
            options={dpLocationDetails?.map((d) => ({ id: d.code, name: d.name })) || []}
          />
          <BoundRichTextField field={formState.specifications} />
          {isOptionCatalogV2Enabled && (
            <>
              <FormHeading title="Pricing" />
              <div css={Css.df.jcsb.pb1.mt2.$}>
                <BoundOptionPricingConfigFields
                  postCutoffDesiredMarginInBasisPoints={
                    formState.optionPricingConfig.postCutoffDesiredMarginInBasisPoints
                  }
                  preCutoffDesiredMarginInBasisPoints={
                    formState.optionPricingConfig.preCutoffDesiredMarginInBasisPoints
                  }
                  selectionFeeInCents={formState.optionPricingConfig.selectionFeeInCents}
                  minimumPriceInCents={formState.optionPricingConfig.minimumPriceInCents}
                  reasonNote={formState.optionPricingConfig.reasonNote}
                />
              </div>
              {/* This component is used in both "Create" and "Edit" modes,
              so we need to to hide the Material Lead Times if we are in edit mode and only use the tab*/}
              {!selectedItem && (
                <>
                  <FormHeading title="Material Lead Times" />
                  <div css={Css.pb1.mt2.$}>
                    {/* Fields for MaterialLeadTimeConfig */}
                    <BoundMaterialLeadTimeConfigFields
                      minLeadTimeInDays={formState.materialLeadTimeConfig.minLeadTimeInDays}
                      expectedLeadTimeInDays={formState.materialLeadTimeConfig.expectedLeadTimeInDays}
                      maxLeadTimeInDays={formState.materialLeadTimeConfig.maxLeadTimeInDays}
                      reasonNote={formState.materialLeadTimeConfig.reasonNote}
                    />
                  </div>
                </>
              )}
            </>
          )}
          {!isReadonly && (
            <div css={Css.df.jcfe.wPx(550).gap1.$}>
              <Button label="Cancel" variant="quaternary" onClick={closeDrawer} />
              <SubmitButton
                form={formState}
                label="Save"
                onClick={async () => {
                  if (formState.canSave()) {
                    const { optionPricingConfig, materialLeadTimeConfig, ...others } = formState.value;

                    await saveItem({
                      variables: {
                        input: {
                          ...others,
                          ...maybeOptionPricingConfig({
                            ...optionPricingConfig,
                            id: selectedItem?.optionPricingConfig?.id,
                          }),
                          ...maybeMaterialLeadTimeConfig({
                            ...materialLeadTimeConfig,
                            id: selectedItem?.materialLeadTimeConfig?.id,
                          }),
                        },
                      },
                    });
                    closeDrawer();
                  }
                }}
              />
            </div>
          )}
        </FormLines>
      </div>
    </>
  );
}

type FormValue = Omit<SaveItemInput, "isSelection"> & {
  optionPricingConfig: OptionPricingFormValue;
  materialLeadTimeConfig: MaterialLeadTimeFormValue;
};

const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  name: { type: "value", rules: [required] },
  costCodeId: { type: "value", rules: [required] },
  materialCodePrefix: { type: "value", rules: [] },
  specifications: { type: "value" },
  unitOfMeasureId: { type: "value", rules: [required] },
  designPackageLocation: { type: "value" },
  optionPricingConfig: { type: "object", config: optionPricingConfigConfig },
  materialLeadTimeConfig: { type: "object", config: materialLeadTimeConfigConfig },
};
